import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Play, RefreshCw, Search, ArrowDownToLine } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { xeroAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage, isXeroSyncInProgressError, getXeroSyncInProgressMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function fmtSkipReasons(reasons) {
  const entries = Object.entries(reasons || {}).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  return entries.map(([reason, n]) => `${reason} ${fmtInt(n)}`).join(', ');
}

const COVERAGE_STATE_LABEL = {
  never_synced: 'Never synced',
  catching_up: 'Catching up',
  steady_state: 'Steady state',
  unmapped_tenant: 'Unmapped tenant',
};

const POLL_INTERVAL_MS = 2000;

export function XeroInvoiceSyncPanel() {
  const [dispatching, setDispatching] = useState(false);
  const [progress, setProgress] = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillPreviewLoading, setBackfillPreviewLoading] = useState(false);
  const [backfillApplyResult, setBackfillApplyResult] = useState(null);
  const [backfillApplyLoading, setBackfillApplyLoading] = useState(false);
  const [backfillError, setBackfillError] = useState(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // Check for an already-running sync on mount.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await xeroAPI.syncStatus();
        if (data.active) {
          setProgress(data);
          if (data.state === 'running') {
            startPolling();
          }
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await xeroAPI.syncStatus();
        setProgress(data);
        if (!data.active || data.state !== 'running') {
          stopPolling();
          if (data.state === 'done') {
            toast.success(`Xero sync complete: ${fmtInt(data.invoices_synced)} invoices across ${fmtInt(data.tenants_completed)} tenants`);
          } else if (data.state === 'partial') {
            const parked = (data.partial_tenants || []).join(', ') || 'a tenant';
            toast.warning(`Xero sync partial: ${parked} still catching up (cursor parked). Run Now again to continue.`);
          } else if (data.state === 'failed') {
            toast.error('Xero invoice sync failed. Check server logs.');
          }
        }
      } catch (err) {
        stopPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const runSync = async () => {
    setDispatching(true);
    setProgress(null);
    try {
      await xeroAPI.sync();
      startPolling();
    } catch (err) {
      if (isXeroSyncInProgressError(err)) {
        // A genuine in-flight run exists -- say so explicitly and start
        // reflecting ITS actual progress instead of silently leaving the
        // panel on whatever it last rendered (Prompt T: this used to look
        // like Run Now had no effect at all).
        toast.info(getXeroSyncInProgressMessage(err));
        try {
          const { data } = await xeroAPI.syncStatus();
          setProgress(data);
          if (data.active && data.state === 'running') startPolling();
        } catch {
          // Best-effort -- the in-progress toast above already told the
          // operator what's going on.
        }
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setDispatching(false);
    }
  };

  const runCoverage = async () => {
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const { data } = await xeroAPI.tenantCoverage();
      setCoverage(data);
    } catch (err) {
      setCoverageError(getErrorMessage(err));
    } finally {
      setCoverageLoading(false);
    }
  };

  const runBackfillPreview = async () => {
    setBackfillPreviewLoading(true);
    setBackfillApplyResult(null);
    setBackfillError(null);
    try {
      const { data } = await xeroAPI.previewBackfill();
      setBackfillPreview(data);
    } catch (err) {
      setBackfillError(getErrorMessage(err));
    } finally {
      setBackfillPreviewLoading(false);
    }
  };

  const runBackfillApply = async () => {
    if (!window.confirm(
      `Reset ${backfillPreview?.count || 0} tenant(s) to a full historical re-walk? ` +
      'The next sync run ignores their last-synced cursor and re-walks from page 1, ' +
      'through the existing bounded/resumable pagination -- no invoices are duplicated ' +
      '(idempotent upsert on external_id).'
    )) return;
    setBackfillApplyLoading(true);
    setBackfillError(null);
    try {
      const { data } = await xeroAPI.applyBackfill();
      setBackfillApplyResult(data);
      runCoverage();
    } catch (err) {
      setBackfillError(getErrorMessage(err));
    } finally {
      setBackfillApplyLoading(false);
    }
  };

  const busy = dispatching || polling;
  const isDone = progress && ['done', 'partial', 'failed'].includes(progress.state);
  const tenants = progress?.tenants || {};
  const tenantList = Object.entries(tenants);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Xero Invoice Sync (All Tenants)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Pulls invoices from every connected Xero tenant and upserts them as
          source:xero documents. Background job with per-tenant progress.
          Also runs on a 6-hour schedule.
        </p>
        <div className="flex gap-2">
          <Button onClick={runSync} disabled={busy} size="sm">
            {(dispatching || polling)
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <Play className="h-4 w-4 mr-2" />
            }
            Run Now
          </Button>
        </div>

        {progress && progress.active && (
          <div className="space-y-4">
            {/* State badge. "partial" is deliberately distinct from
                "done" -- a run that parked a tenant mid-catchup is not
                complete, and rendering it as COMPLETE (green) was the
                dishonesty Prompt T flagged: the operator had no way to
                tell br_ltd still needed another Run Now. */}
            <div className="flex items-center gap-2">
              <Badge className={`text-[10px] ${
                progress.state === 'done'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : progress.state === 'partial'
                  ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                  : progress.state === 'failed'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
              }`}>
                {progress.state === 'done' ? 'COMPLETE'
                  : progress.state === 'partial' ? 'PARTIAL'
                  : progress.state === 'failed' ? 'FAILED'
                  : 'RUNNING'}
              </Badge>
              {progress.state === 'running' && (
                <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* Partial-run honesty: which tenant(s) are still parked and
                need another Run Now to keep catching up. */}
            {progress.state === 'partial' && progress.partial_tenants && progress.partial_tenants.length > 0 && (
              <div
                className="rounded p-2 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900 text-xs text-orange-700 dark:text-orange-300"
                data-testid="xero-sync-partial-notice"
              >
                Partial: {progress.partial_tenants.join(', ')} cursor parked mid-catchup ({fmtInt(progress.invoices_synced)} synced this run). Run Now again to continue.
              </div>
            )}

            {/* Skip reasons: report-only breakdown of why invoices_skipped
                isn't zero (voided/deleted/draft), not a change to what
                gets skipped. */}
            {fmtSkipReasons(progress.skip_reasons) && (
              <p className="text-[10px] text-muted-foreground" data-testid="xero-sync-skip-reasons">
                Skipped this run: {fmtSkipReasons(progress.skip_reasons)}
              </p>
            )}

            {/* Tenant progress */}
            {progress.tenants_total > 0 && (
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                  <span>Tenants: {fmtInt(progress.tenants_completed)} / {fmtInt(progress.tenants_total)}</span>
                  <span>Invoices synced: {fmtInt(progress.invoices_synced)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (progress.tenants_completed / progress.tenants_total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Unresolved tenants: no known entity mapping, skipped entirely
                this run rather than guessing an entity_id. */}
            {progress.unresolved_tenants && progress.unresolved_tenants.length > 0 && (
              <div className="rounded p-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300">
                {progress.unresolved_tenants.length} tenant(s) matched no known entity mapping and were
                skipped this run: {progress.unresolved_tenants.map(t => t.tenant_name).join(', ')}
              </div>
            )}

            {/* Per-tenant breakdown */}
            {tenantList.length > 0 && (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Tenant</th>
                      <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                      <th className="text-right px-2 py-1.5 font-medium">Synced</th>
                      <th className="text-right px-2 py-1.5 font-medium">Skipped</th>
                      <th className="text-left px-2 py-1.5 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenantList.map(([name, t]) => (
                      <tr key={name} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono text-[10px]">{name}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">
                          {t.state === 'unresolved'
                            ? <Badge variant="destructive" className="text-[10px]">UNRESOLVED</Badge>
                            : (t.entity_id || '-')}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmtInt(t.count)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {fmtInt(t.skipped)}
                          {fmtSkipReasons(t.skip_reasons) && (
                            <div className="text-[9px] text-muted-foreground font-normal whitespace-nowrap">
                              {fmtSkipReasons(t.skip_reasons)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className={`text-[10px] ${
                            t.state === 'done' ? 'text-green-600'
                            : t.state === 'partial' ? 'text-orange-600'
                            : t.state === 'error' || t.state === 'unresolved' ? 'text-red-600'
                            : 'text-yellow-600'
                          }`}>
                            {t.state}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Errors */}
            {progress.errors && progress.errors.length > 0 && (
              <div className="text-xs text-red-600 space-y-1">
                {progress.errors.map((e, i) => (
                  <p key={i}>{e}</p>
                ))}
              </div>
            )}

            {progress.started_at && (
              <p className="text-[10px] text-muted-foreground">
                Started: {progress.started_at}
                {progress.finished_at && ` | Finished: ${progress.finished_at}`}
              </p>
            )}
          </div>
        )}

        <hr className="border-muted" />

        {/* Tenant coverage: per-tenant reason a tenant's invoices might be
            missing (unmapped, never synced, still catching up) -- run
            against prod before assuming the backfill window is needed. */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Tenant coverage</p>
            <Button
              variant="outline" size="sm" onClick={runCoverage} disabled={coverageLoading}
              data-testid="xero-tenant-coverage-button"
            >
              {coverageLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Search className="h-3 w-3 mr-1" />}
              Check coverage
            </Button>
          </div>

          {coverageError && (
            <p className="text-xs text-red-600" data-testid="xero-tenant-coverage-error">{coverageError}</p>
          )}

          {coverage && !coverageError && (
            coverage.xero_connected ? (
              <div className="overflow-x-auto border rounded-lg" data-testid="xero-tenant-coverage-result">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Tenant</th>
                      <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                      <th className="text-left px-2 py-1.5 font-medium">Coverage</th>
                      <th className="text-left px-2 py-1.5 font-medium">Last synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(coverage.tenants || []).map((t) => (
                      <tr key={t.tenant_id} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono text-[10px]">{t.tenant_name}</td>
                        <td className="px-2 py-1 font-mono text-[10px]">
                          {t.coverage_state === 'unmapped_tenant'
                            ? <Badge variant="destructive" className="text-[10px]">UNMAPPED</Badge>
                            : (t.entity_id || '-')}
                        </td>
                        <td className="px-2 py-1">
                          <Badge variant="outline" className={`text-[10px] ${
                            t.coverage_state === 'steady_state' ? 'text-green-600'
                            : t.coverage_state === 'unmapped_tenant' ? 'text-red-600'
                            : 'text-yellow-600'
                          }`}>
                            {COVERAGE_STATE_LABEL[t.coverage_state] || t.coverage_state}
                            {t.force_backfill ? ' (backfill queued)' : ''}
                          </Badge>
                        </td>
                        <td className="px-2 py-1 text-[10px] text-muted-foreground">{t.last_synced_at || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="xero-tenant-coverage-not-connected">
                Xero is not connected.
              </p>
            )
          )}
        </div>

        {/* Backfill window: reset a tenant's sync cursor to force a full
            historical re-walk on the next sync run, once coverage above
            shows a real gap (never synced / still catching up). */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Backfill window</p>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm" onClick={runBackfillPreview} disabled={backfillPreviewLoading}
                data-testid="xero-backfill-preview"
              >
                {backfillPreviewLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ArrowDownToLine className="h-3 w-3 mr-1" />}
                Preview
              </Button>
              {backfillPreview && !backfillPreview.error && backfillPreview.count > 0 && (
                <Button
                  variant="default" size="sm" onClick={runBackfillApply}
                  disabled={backfillApplyLoading || !!backfillApplyResult}
                  data-testid="xero-backfill-apply"
                >
                  {backfillApplyLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Apply ({backfillPreview.count})
                </Button>
              )}
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Resets every connected tenant's sync cursor so the next run does a full historical
            re-walk instead of an incremental one -- never calls Xero itself, and never duplicates
            invoices (idempotent upsert on external_id).
          </p>

          {backfillError && (
            <p className="text-xs text-red-600" data-testid="xero-backfill-error">{backfillError}</p>
          )}

          {backfillPreview && !backfillError && (
            <p className="text-xs" data-testid="xero-backfill-preview-result">
              {backfillPreview.count} tenant(s) would be reset to a full historical re-walk.
            </p>
          )}

          {backfillApplyResult && !backfillError && (
            <p className="text-xs text-green-700 dark:text-green-400" data-testid="xero-backfill-apply-result">
              Queued: {(backfillApplyResult.applied || []).join(', ') || 'none'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
